import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import VectorLayer from 'ol/layer/Vector';
import type Map from 'ol/Map';
import VectorSource from 'ol/source/Vector';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { mapCoordinateFor, type GeoCoordinate } from '../domain/coordinates';

/** Zoom used to inspect a searched coordinate; a closer current zoom is kept. */
export const LOCATION_INSPECTION_ZOOM = 17;
/** Above every data layer (CASUR reference, Maestro, working layers), below editing previews (≥ 10 000). */
export const LOCATION_LAYER_Z_INDEX = 9_000;

// Neutral halo + small solid dot: legible over satellite imagery and the cartographic basemap without shouting.
const MARKER_STYLE = [
  new Style({ image: new CircleStyle({ radius: 12, fill: new Fill({ color: 'rgba(17,158,221,.16)' }), stroke: new Stroke({ color: 'rgba(11,36,51,.55)', width: 1.25 }) }) }),
  new Style({ image: new CircleStyle({ radius: 5.5, fill: new Fill({ color: '#119edd' }), stroke: new Stroke({ color: '#ffffff', width: 2.5 }) }) }),
];

/**
 * A temporary cartographic location (search result or "Marcar ubicación"). It is UI state only: it is not a CASUR
 * entity, never touches Selection/SelectionSet/WorkScope, does not mark the project dirty and is not persisted.
 */
export class MapLocationController {
  readonly layer: VectorLayer<VectorSource<Feature<Point>>>;
  private readonly source = new VectorSource<Feature<Point>>();
  private location: GeoCoordinate | null = null;
  private readonly listeners = new Set<(location: GeoCoordinate | null) => void>();

  constructor(private readonly map: Map) {
    this.layer = new VectorLayer({ source: this.source, style: MARKER_STYLE, properties: { role: 'map-location' } });
    this.layer.setZIndex(LOCATION_LAYER_Z_INDEX);
    map.addLayer(this.layer);
  }

  get current(): GeoCoordinate | null { return this.location; }

  onChange(listener: (location: GeoCoordinate | null) => void): void { this.listeners.add(listener); }

  /** Places the single active marker (replacing any previous one); `inspect` also centers and zooms to it. */
  mark(location: GeoCoordinate, options: { readonly inspect?: boolean } = {}): void {
    this.location = location;
    this.source.clear(true);
    this.source.addFeature(new Feature({ geometry: new Point(mapCoordinateFor(location)) }));
    if (options.inspect) this.zoomTo();
    this.listeners.forEach((listener) => listener(location));
  }

  clear(): void {
    if (!this.location) return;
    this.location = null;
    this.source.clear(true);
    this.listeners.forEach((listener) => listener(null));
  }

  zoomTo(): void {
    if (!this.location) return;
    const view = this.map.getView();
    view.animate({ center: mapCoordinateFor(this.location), zoom: Math.max(view.getZoom() ?? 0, LOCATION_INSPECTION_ZOOM), duration: 350 });
  }

  /** Centers on a coordinate keeping the current zoom. */
  centerOn(location: GeoCoordinate): void {
    this.map.getView().animate({ center: mapCoordinateFor(location), duration: 250 });
  }
}
